import { parseFlexibleDate } from '../../../utils/dateUtils';
import { resolveProgramName } from '../../../utils/dataNormalizer';
import { getFirstCaseInsensitiveValue } from './appsheetFieldUtils';

const normalizeStatus = (value) => String(value ?? '').trim().toLowerCase();

const isSameMonth = (left, right) =>
  left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth();

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

const monthAfter = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 1);

const isCancelledStatus = (value) => {
  const normalized = normalizeStatus(value);
  if (!normalized) return false;
  if (normalized.includes('permanent') && normalized.includes('closed')) return true;
  if (normalized.includes('permanant') && normalized.includes('closed')) return true;
  if (normalized.includes('cancelled') || normalized.includes('canceled')) return true;
  if (normalized === 'cancel') return true;
  return false;
};

const isPendingLiveStatus = (value) => {
  const normalized = normalizeStatus(value);
  if (!normalized) return false;
  return normalized.includes('pending') && normalized.includes('live');
};

const resolveOrderItemStatus = (row) =>
  getFirstCaseInsensitiveValue(row, [
    'status',
    'Status',
    'Status__c',
    'Order Status',
    'orderStatus',
    'order_status',
    'VmtCallStatus',
    'vmtcallstatus',
  ]);

const resolveOrderItemProgram = (row) =>
  getFirstCaseInsensitiveValue(row, [
    'productProgram',
    'ProductProgram',
    'productprogram',
    'productProgramName',
    'product_program',
    'Product Program',
    'product_program_name',
    'Product_Program',
    'Product_Program__c',
    'Product Program Name',
    'Program Name',
    'programName',
    'program_name',
  ]);

const resolveOrderItemDealerPrice = (row) =>
  getFirstCaseInsensitiveValue(row, [
    'dealerPrice',
    'DealerPrice',
    'dealer_price',
    'Dealer Price',
    'Dealer_Price',
  ]);

const resolveLiveDate = (row) =>
  getFirstCaseInsensitiveValue(row, [
    'effectiveDate',
    'EffectiveDate',
    'selectionDate',
    'SelectionDate',
    'billingDate',
    'BillingDate',
    'billingStartDate',
    'BillingStartDate',
    'goLiveDate',
    'GoLiveDate',
    'liveDate',
    'LiveDate',
    'startDate',
    'StartDate',
    'Go Live Date',
    'Live Date',
  ]);

const resolveCancelDate = (row) =>
  getFirstCaseInsensitiveValue(row, [
    'cancelDate',
    'CancelDate',
    'cancelledDate',
    'CancelledDate',
    'cancellationDate',
    'CancellationDate',
    'endDate',
    'EndDate',
    'billingEndDate',
    'BillingEndDate',
    'terminationDate',
    'TerminatedDate',
    'SBQQ__TerminatedDate__c',
  ]);

const resolveSsdDate = (row) =>
  getFirstCaseInsensitiveValue(row, [
    'ssd',
    'SSD',
    'siteStartDate',
    'SiteStartDate',
    'site_start_date',
    'siteStart',
    'Site Start Date',
    'Website Start Date',
    'websiteStartDate',
    'website_start_date',
    'SsdDate',
    'Service_Start_Date__c',
  ]);

const parsePrice = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const cleaned = String(value).replace(/[^0-9.-]+/g, '');
  if (!cleaned) return 0;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

const resolveProgram = (row) => resolveProgramName(resolveOrderItemProgram(row));

const matchesProgram = (rowProgram, targetProgram) => {
  if (!targetProgram) return true;
  return rowProgram === targetProgram;
};

const isBillableByStartRules = (programName, liveDate, ssdDate, now) => {
  if (!programName) return false;
  const currentMonthStart = startOfMonth(now);

  if (programName === 'Website') {
    if (ssdDate) {
      const ssdParsed = parseFlexibleDate(ssdDate);
      if (!ssdParsed) return false;
      const billableStart = startOfMonth(monthAfter(ssdParsed));
      return currentMonthStart >= billableStart;
    }
    return false;
  }

  const liveParsed = parseFlexibleDate(liveDate);
  if (!liveParsed) return false;

  if (startOfMonth(liveParsed) < currentMonthStart) return true;
  if (startOfMonth(liveParsed) > currentMonthStart) return false;

  // Same month as current.
  return liveParsed.getDate() < 15;
};

const applyCancelRules = (baseBillable, cancelDate, now) => {
  if (!baseBillable) return false;
  const cancelParsed = parseFlexibleDate(cancelDate);
  if (!cancelParsed) return true;

  const currentMonthStart = startOfMonth(now);
  if (startOfMonth(cancelParsed) < currentMonthStart) return false;

  if (isSameMonth(cancelParsed, now)) {
    return cancelParsed.getDate() >= 15;
  }

  return true;
};

export const isOrderItemBillable = (row, { targetProgram = null, now = new Date() } = {}) => {
  if (!row) return false;

  const status = resolveOrderItemStatus(row);
  if (isCancelledStatus(status) || isPendingLiveStatus(status)) return false;

  const rowProgram = resolveProgram(row);
  if (!matchesProgram(rowProgram, targetProgram)) return false;

  const liveDate = resolveLiveDate(row);
  const cancelDate = resolveCancelDate(row);
  const ssdDate = resolveSsdDate(row);

  const baseBillable = isBillableByStartRules(rowProgram, liveDate, ssdDate, now);
  return applyCancelRules(baseBillable, cancelDate, now);
};

export const getOrderItemDealerPrice = (row) => parsePrice(resolveOrderItemDealerPrice(row));

export const getOrderItemProgramName = (row) => resolveProgram(row);

export const sumOrderItemsMrr = (rows = [], { targetProgram = null, now = new Date() } = {}) =>
  (rows || []).reduce((sum, row) => {
    if (!isOrderItemBillable(row, { targetProgram, now })) return sum;
    return sum + getOrderItemDealerPrice(row);
  }, 0);

export const isAccountCancelled = (status) => isCancelledStatus(status);
